import axios from 'axios'
import { BASE_URL } from '../constants/config'
import { getJwtToken, clearJwtTokenCache } from '../utils/jiraContext'
import { emitLicenseRequired } from '../utils/licenseManager'

/**
 * LIBRARY API CLIENT
 *
 * Axios instance configured for library-related API calls.
 * Handles library folders and library scenarios (templates).
 * Library items have isLibrary=true and release=null.
 */
const apiClient = axios.create({
  baseURL: BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  },
  timeout: 10000
})

/**
 * REQUEST INTERCEPTOR
 * Attaches JWT token to all outgoing requests.
 */
apiClient.interceptors.request.use(
  async (config) => {
    try {
      const token = await getJwtToken()
      if (token) {
        config.headers.Authorization = `Bearer ${token}`
      }
    } catch (error) {
      console.error('Failed to get JWT token:', error)
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

/**
 * RESPONSE INTERCEPTOR
 * Handles global error responses, especially authentication failures.
 */
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('Library API Error:', {
      url: error.config?.url,
      method: error.config?.method,
      status: error.response?.status,
      message: error.response?.data?.message || error.message
    })

    // Handle license validation failures - 402 means license is invalid/expired
    if (error.response?.status === 402) {
      emitLicenseRequired(error.response?.data?.message || 'License required')
    }

    if (error.response?.status === 401) {
      console.error('Unauthorized access - clearing token cache')
      clearJwtTokenCache()
    }

    return Promise.reject(error)
  }
)

// ============================================================================
// LIBRARY FOLDER API FUNCTIONS
// ============================================================================

/**
 * Create a new library folder
 *
 * @param {Object} folderData - Folder data
 * @param {string} folderData.name - Folder name (max 255 characters)
 * @param {number} folderData.jiraProjectId - JIRA project ID
 * @param {number|null} folderData.parentId - Parent library folder ID (null for root)
 * @param {number} folderData.orderIndex - Sort order (optional, auto-assigned if not provided)
 * @returns {Promise<Object>} Created library folder object
 */
export const createLibraryFolder = async (folderData) => {
  try {
    const response = await apiClient.post('/library/folders', folderData)
    return response.data
  } catch (error) {
    console.error('Error creating library folder:', error)
    throw error
  }
}

/**
 * Fetch flat list of library folders for a project
 *
 * @param {number|string} jiraProjectId - JIRA project ID
 * @returns {Promise<Array>} Flat array of library folder objects
 */
export const fetchLibraryFolders = async (jiraProjectId) => {
  try {
    const response = await apiClient.get(`/library/folders/project/${jiraProjectId}`)
    return response.data
  } catch (error) {
    console.error('Error fetching library folders:', error)
    throw error
  }
}

/**
 * Fetch hierarchical tree of library folders for a project
 *
 * @param {number|string} jiraProjectId - JIRA project ID
 * @returns {Promise<Array>} Hierarchical array of library folder objects with children
 */
export const fetchLibraryFolderTree = async (jiraProjectId) => {
  try {
    const response = await apiClient.get(`/library/folders/project/${jiraProjectId}/tree`)
    return response.data
  } catch (error) {
    console.error('Error fetching library folder tree:', error)
    throw error
  }
}

/**
 * Update an existing library folder
 *
 * @param {number|string} folderId - Library folder ID
 * @param {Object} updates - Fields to update { name?, orderIndex? }
 * @returns {Promise<Object>} Updated library folder object
 */
export const updateLibraryFolder = async (folderId, updates) => {
  try {
    const response = await apiClient.put(`/library/folders/${folderId}`, updates)
    return response.data
  } catch (error) {
    console.error('Error updating library folder:', error)
    throw error
  }
}

/**
 * Delete a library folder
 * Note: This cascade deletes all nested content (child folders, scenarios, test cases, steps)
 *
 * @param {number|string} folderId - Library folder ID
 * @returns {Promise<void>}
 */
export const deleteLibraryFolder = async (folderId) => {
  try {
    const response = await apiClient.delete(`/library/folders/${folderId}`)
    return response.data
  } catch (error) {
    console.error('Error deleting library folder:', error)
    throw error
  }
}

/**
 * Fetch library scenarios in a specific library folder
 *
 * @param {number|string} folderId - Library folder ID
 * @returns {Promise<Array>} Array of library test scenario objects
 */
export const fetchLibraryScenariosInFolder = async (folderId) => {
  try {
    const response = await apiClient.get(`/library/folders/${folderId}/testscenarios`)
    return response.data
  } catch (error) {
    console.error('Error fetching library scenarios in folder:', error)
    throw error
  }
}

/**
 * Copy a library folder (with all nested content) to a release
 *
 * @param {number|string} folderId - Library folder ID to copy
 * @param {Object} copyData - Copy target data
 * @param {number} copyData.targetReleaseId - Target release ID
 * @param {number|null} copyData.targetParentFolderId - Target parent folder ID in release (null for root)
 * @returns {Promise<Object>} Created release folder object
 */
export const copyLibraryFolderToRelease = async (folderId, copyData) => {
  try {
    const response = await apiClient.post(`/library/folders/${folderId}/copy`, copyData)
    return response.data
  } catch (error) {
    console.error('Error copying library folder to release:', error)
    throw error
  }
}

/**
 * Move a library folder to a new parent or reorder
 *
 * @param {number|string} folderId - Library folder ID
 * @param {Object} moveData - Move data { newParentId, orderIndex }
 * @returns {Promise<Object>} Updated library folder object
 */
export const moveLibraryFolder = async (folderId, moveData) => {
  try {
    const response = await apiClient.put(`/library/folders/${folderId}/move`, moveData)
    return response.data
  } catch (error) {
    console.error('Error moving library folder:', error)
    throw error
  }
}

/**
 * Reorder library folders within a parent
 * Sends only the dragged folder's new orderIndex — the backend places it at
 * that position and reindexes every sibling in the scope to a contiguous 1..N.
 *
 * The previous implementation fanned out N parallel PUTs (one per sibling) via
 * Promise.all, which let the backend interleave updates from the same drop and
 * produce duplicate orderIndex values. A single PUT matches the release-folder
 * pattern and relies on the backend's transactional reindex logic.
 *
 * @param {Array} reorderData - Array with a single { id, orderIndex } object for the dragged folder
 * @returns {Promise<Array>} Updated folder data
 */
export const reorderLibraryFolders = async (reorderData) => {
  try {
    const { id, orderIndex } = reorderData[0]
    const response = await apiClient.put(`/library/folders/${id}`, { orderIndex })
    return [response.data]
  } catch (error) {
    console.error('Error reordering library folders:', error)
    throw error
  }
}

// ============================================================================
// LIBRARY SCENARIO API FUNCTIONS
// ============================================================================

/**
 * Create a new library test scenario
 *
 * @param {Object} scenarioData - Scenario data
 * @param {string} scenarioData.scenarioName - Scenario name
 * @param {string} scenarioData.description - Description (optional)
 * @param {string} scenarioData.priority - Priority: Low, Medium, High, or Critical
 * @param {number} scenarioData.jiraProjectId - JIRA project ID
 * @param {number|null} scenarioData.folderId - Library folder ID (null for root level)
 * @param {number} scenarioData.orderIndex - Sort order (optional, auto-assigned if not provided)
 * @returns {Promise<Object>} Created library test scenario object
 */
export const createLibraryScenario = async (scenarioData) => {
  try {
    const response = await apiClient.post('/library/testscenarios', scenarioData)
    return response.data
  } catch (error) {
    console.error('Error creating library scenario:', error)
    throw error
  }
}

/**
 * Fetch all library test scenarios for a project
 *
 * @param {number|string} jiraProjectId - JIRA project ID
 * @returns {Promise<Array>} Array of library test scenario objects
 */
export const fetchAllLibraryScenarios = async (jiraProjectId) => {
  try {
    const response = await apiClient.get(`/library/testscenarios/project/${jiraProjectId}`)
    return response.data
  } catch (error) {
    console.error('Error fetching all library scenarios:', error)
    throw error
  }
}

/**
 * Update an existing library test scenario
 *
 * @param {number|string} scenarioId - Library scenario ID
 * @param {Object} updates - Fields to update { scenarioName?, description?, priority?, assignee?, orderIndex? }
 * @returns {Promise<Object>} Updated library test scenario object
 */
export const updateLibraryScenario = async (scenarioId, updates) => {
  try {
    const response = await apiClient.put(`/library/testscenarios/${scenarioId}`, updates)
    return response.data
  } catch (error) {
    console.error('Error updating library scenario:', error)
    throw error
  }
}

/**
 * Delete a library test scenario
 * Note: This cascade deletes all test cases, steps, and attachments
 *
 * @param {number|string} scenarioId - Library scenario ID
 * @returns {Promise<void>}
 */
export const deleteLibraryScenario = async (scenarioId) => {
  try {
    const response = await apiClient.delete(`/library/testscenarios/${scenarioId}`)
    return response.data
  } catch (error) {
    console.error('Error deleting library scenario:', error)
    throw error
  }
}

/**
 * Clone a library scenario in place (same library folder).
 * Backend duplicates all test cases, steps, attachments, and labels, and
 * appends " (Copy)" to the scenario name. The clone stays in the library.
 *
 * @param {number|string} scenarioId - Library scenario ID to clone
 * @returns {Promise<Object>} Cloned library scenario object
 */
export const cloneLibraryScenario = async (scenarioId) => {
  try {
    const response = await apiClient.post(`/library/testscenarios/${scenarioId}/clone`)
    return response.data
  } catch (error) {
    console.error('Error cloning library scenario:', error)
    throw error
  }
}

/**
 * Copy a single library scenario to a release folder
 *
 * @param {number|string} scenarioId - Library scenario ID to copy
 * @param {Object} copyData - Copy target data
 * @param {number} copyData.targetReleaseId - Target release ID
 * @param {number} copyData.targetFolderId - Target folder ID in the release
 * @returns {Promise<Object>} Created release scenario object
 */
export const copyLibraryScenarioToRelease = async (scenarioId, copyData) => {
  try {
    const response = await apiClient.post(`/library/testscenarios/${scenarioId}/copy`, copyData)
    return response.data
  } catch (error) {
    console.error('Error copying library scenario to release:', error)
    throw error
  }
}

/**
 * Bulk copy multiple library scenarios to a release folder
 *
 * @param {Object} bulkCopyData - Bulk copy data
 * @param {number[]} bulkCopyData.scenarioIds - Array of library scenario IDs to copy
 * @param {number} bulkCopyData.targetReleaseId - Target release ID
 * @param {number} bulkCopyData.targetFolderId - Target folder ID in the release
 * @returns {Promise<Array>} Array of created release scenario objects
 */
export const bulkCopyLibraryScenariosToRelease = async (bulkCopyData) => {
  try {
    const response = await apiClient.post('/library/testscenarios/bulk-copy', bulkCopyData)
    return response.data
  } catch (error) {
    console.error('Error bulk copying library scenarios to release:', error)
    throw error
  }
}

/**
 * Move a library scenario to a different library folder
 *
 * @param {number|string} scenarioId - Library scenario ID
 * @param {Object} moveData - Move data { folderId, orderIndex }
 * @returns {Promise<Object>} Updated library scenario object
 */
export const moveLibraryScenarioToFolder = async (scenarioId, moveData) => {
  try {
    const response = await apiClient.put(
      `/library/testscenarios/${scenarioId}/move`,
      moveData
    )
    return response.data
  } catch (error) {
    console.error('Error moving library scenario:', error)
    throw error
  }
}

/**
 * Reorder library scenarios within a folder
 * Updates the orderIndex for each scenario in the list
 *
 * @param {Array} reorderData - Array of { id, orderIndex } objects
 * @returns {Promise<Array>} Updated scenarios
 */
export const reorderLibraryScenarios = async (reorderData) => {
  try {
    // Batch update - update each scenario's orderIndex
    const updatePromises = reorderData.map(({ id, orderIndex }) =>
      apiClient.put(`/library/testscenarios/${id}`, { orderIndex })
    )
    const responses = await Promise.all(updatePromises)
    return responses.map(r => r.data)
  } catch (error) {
    console.error('Error reordering library scenarios:', error)
    throw error
  }
}
